import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/MainLayout';
import { API } from '../config/api';
import {
  CalendarDays, Clock, Check, XCircle, Building2, Users,
  Filter, X, ChevronRight, TrendingUp, MapPin, Cloud, RefreshCw, Loader2
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Legend
} from 'recharts';

const DashboardPage = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activePreset, setActivePreset] = useState('all');
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchStats();
    if (user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'operator') {
      fetchSyncStatus();
    }
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const res = await axios.get(`${API}/salesforce/sync-status`);
      setSyncStatus(res.data);
    } catch (e) {
      console.error('Sync status error:', e);
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/salesforce/sync-now`);
      if (res.data?.sites_updated > 0) {
        toast.success(res.data.message, { duration: 6000 });
      } else {
        toast.success('Sincronizzazione Salesforce completata');
      }
      await fetchSyncStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  const fetchStats = async (from, to) => {
    try {
      setLoading(true);
      let url = `${API}/reports/dashboard/stats`;
      const params = [];
      if (from) params.push(`date_from=${from}`);
      if (to) params.push(`date_to=${to}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await axios.get(url);
      setStats(res.data);
    } catch (e) {
      toast.error(t('errors.loadStats'));
    } finally {
      setLoading(false);
    }
  };

  const applyDateFilter = () => {
    setActivePreset('custom');
    fetchStats(dateFrom, dateTo);
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setActivePreset('all');
    fetchStats();
  };

  const applyPreset = (preset) => {
    const now = new Date();
    let from = '', to = '';
    switch (preset) {
      case 'today':
        from = to = now.toISOString().split('T')[0];
        break;
      case 'week': {
        const monday = new Date(now);
        monday.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
        from = monday.toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
        break;
      }
      case 'month':
        from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        to = now.toISOString().split('T')[0];
        break;
      case 'year':
        from = `${now.getFullYear()}-01-01`;
        to = now.toISOString().split('T')[0];
        break;
      default:
        break;
    }
    setDateFrom(from);
    setDateTo(to);
    setActivePreset(preset);
    if (preset === 'all') {
      fetchStats();
    } else {
      fetchStats(from, to);
    }
  };

  const presets = [
    { key: 'all', label: 'Tutto' },
    { key: 'today', label: 'Oggi' },
    { key: 'week', label: 'Settimana' },
    { key: 'month', label: 'Mese' },
    { key: 'year', label: 'Anno' },
  ];

  const today = new Date().toISOString().split('T')[0];

  const statCards = [
    { label: t('dashboard.totalAppointments'), value: stats?.total_appointments || 0, icon: CalendarDays, color: 'bg-blue-500', action: () => navigate('/calendar'), hint: 'Vai al calendario' },
    { label: t('dashboard.scheduled'), value: stats?.scheduled || 0, icon: Clock, color: 'bg-yellow-500', action: () => navigate(`/calendar?date=${today}`), hint: 'Appuntamenti di oggi' },
    { label: t('dashboard.completed'), value: stats?.completed || 0, icon: Check, color: 'bg-green-500' },
    { label: t('dashboard.notCompleted'), value: stats?.not_completed || 0, icon: XCircle, color: 'bg-red-500' },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-3 py-2 shadow-lg rounded-lg border text-sm">
          <p className="font-medium">{payload[0].name || payload[0].payload?.day}</p>
          <p className="text-gray-600">{payload[0].value} appuntamenti</p>
        </div>
      );
    }
    return null;
  };

  const RADIAN = Math.PI / 180;
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-xs font-bold">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <MainLayout>
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800" data-testid="dashboard-title">{t('dashboard.title')}</h1>
            <p className="text-gray-600 mt-1">{t('dashboard.welcome')}, {user?.first_name}!</p>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition text-sm font-medium ${
              showFilters || activePreset !== 'all' ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm'
            }`}
            data-testid="dashboard-filter-toggle"
          >
            <Filter size={16} />
            Filtra per data
            {activePreset !== 'all' && (
              <span className="bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">!</span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 bg-white rounded-xl shadow-sm p-4 border" data-testid="dashboard-date-filters">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {presets.map(p => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activePreset === p.key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
              {activePreset !== 'all' && (
                <button onClick={clearFilters} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded-lg hover:bg-gray-50" data-testid="clear-date-filter">
                  <X size={14} /> Reset
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Da</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="date-from" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">A</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="date-to" />
              </div>
              <button onClick={applyDateFilter} disabled={!dateFrom && !dateTo} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 transition" data-testid="apply-date-filter">
                Applica
              </button>
            </div>
            {activePreset !== 'all' && (dateFrom || dateTo) && (
              <p className="mt-2 text-xs text-blue-600">Periodo: {dateFrom || '...'} - {dateTo || '...'}</p>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lojack-red"></div>
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statCards.map((stat, idx) => (
              <div
                key={idx}
                onClick={stat.action}
                className={`bg-white rounded-xl shadow-sm p-6 transition-all ${
                  stat.action ? 'cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98]' : ''
                }`}
                data-testid={`stat-card-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{stat.label}</p>
                    <p className="text-3xl font-bold text-gray-800 mt-1">{stat.value}</p>
                    {stat.hint && (
                      <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                        {stat.hint} <ChevronRight size={12} />
                      </p>
                    )}
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <stat.icon className="text-white" size={24} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Salesforce Sync Status Panel */}
          {(user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'operator') && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border-l-4 border-blue-500" data-testid="salesforce-sync-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Cloud size={20} className="text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Salesforce Sync</h3>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Automatico ogni 15 min</span>
                </div>
                {(user?.role === 'admin' || user?.role === 'superadmin') && (
                  <button
                    onClick={triggerSync}
                    disabled={syncing}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 transition"
                    data-testid="sync-now-btn"
                  >
                    {syncing ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    {syncing ? 'Sincronizzazione...' : 'Sincronizza ora'}
                  </button>
                )}
              </div>
              {syncStatus?.last_sync ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Ultimo sync</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {new Date(syncStatus.last_sync.timestamp).toLocaleString('it-IT', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Stato</p>
                    <p className={`text-sm font-semibold ${syncStatus.last_sync.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {syncStatus.last_sync.status === 'success' ? 'Successo' : 'Errore'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Appuntamenti verificati</p>
                    <p className="text-sm font-semibold text-gray-800">{syncStatus.last_sync.appointments_checked || 0}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Aggiornati (ultime 10 sync)</p>
                    <p className="text-sm font-semibold text-blue-600">{syncStatus.total_updated_last_10 || 0}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400">Nessun sync eseguito ancora</p>
              )}
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Status Pie Chart */}
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="status-chart">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Distribuzione Stati</h3>
              {stats?.status_distribution?.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={stats.status_distribution.filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={110}
                      paddingAngle={3}
                      dataKey="value"
                      labelLine={false}
                      label={renderCustomLabel}
                    >
                      {stats.status_distribution.filter(d => d.value > 0).map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[280px] text-gray-400">
                  Nessun dato disponibile
                </div>
              )}
            </div>

            {/* Weekly Trend Bar Chart */}
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="weekly-trend-chart">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp size={20} className="text-blue-500" />
                Trend Ultimi 7 Giorni
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stats?.weekly_trend || []} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="appuntamenti" fill="#3B82F6" radius={[6, 6, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Second Row */}
          {user?.role !== 'installer' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Team Performance */}
              <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2" data-testid="team-performance">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Building2 size={20} className="text-lojack-red" />
                  Performance Team
                </h3>
                {stats?.team_performance?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.team_performance.map((team, i) => {
                      const maxVal = stats.team_performance[0]?.appuntamenti || 1;
                      const pct = (team.appuntamenti / maxVal) * 100;
                      return (
                        <div key={i}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700 truncate mr-2">{team.team}</span>
                            <span className="text-gray-500 whitespace-nowrap">{team.appuntamenti}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-lojack-red to-red-400 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 text-gray-400">
                    Nessun team con appuntamenti
                  </div>
                )}
              </div>

              {/* Quick Stats */}
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('dashboard.activeTeams')}</h3>
                  <div className="flex items-center justify-between">
                    <Building2 className="text-lojack-red" size={40} />
                    <span className="text-4xl font-bold text-gray-800">{stats?.teams_count || 0}</span>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('dashboard.registeredUsers')}</h3>
                  <div className="flex items-center justify-between">
                    <Users className="text-lojack-red" size={40} />
                    <span className="text-4xl font-bold text-gray-800">{stats?.users_count || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upcoming Appointments */}
          {stats?.upcoming_appointments?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="upcoming-appointments">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Clock size={20} className="text-yellow-500" />
                Prossimi Appuntamenti
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Orario</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Luogo</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Team</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {stats.upcoming_appointments.map((apt, i) => (
                      <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/calendar')}>
                        <td className="py-3 px-4 font-medium text-gray-800 text-sm">{apt.client_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {apt.scheduled_date ? new Date(apt.scheduled_date).toLocaleDateString('it-IT') : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {apt.scheduled_time || '-'}
                          {apt.scheduled_end_time && (
                            <span className="text-gray-400"> - {apt.scheduled_end_time}</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 flex items-center gap-1">
                          <MapPin size={14} className="text-gray-400" />
                          {apt.city || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                            {apt.team_name || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </MainLayout>
  );
};

export default DashboardPage;
